import React from "react";

/**
 * Wraps a React state setter (useState, Context, or useReducer dispatch) to log
 * updates in development.
 *
 * @param label - A string label or a function that receives the dispatched value
 *                and returns a string — useful for reducers where the label
 *                should reflect the resulting state.
 * @param setState - The original React setState or dispatch function
 * @returns A wrapped setter that logs updates with stack traces (dev-only)
 */
export function createDebugSetter<T>(
  label: string | ((value: T) => string),
  setState: React.Dispatch<T>,
): React.Dispatch<T> {
  if (process.env.NODE_ENV !== "development") {
    // 🧱 In production: return the original setter unchanged
    return setState;
  }

  // 🧰 In development: wrap setter to log updates
  return (newValue: T) => {
    const resolvedLabel = typeof label === "function" ? label(newValue) : label;
    console.groupCollapsed(
      `%c🔄 ${resolvedLabel}`,
      "color: #3e5ea9; font-weight: bold;",
    );
    console.debug("New value:", newValue);
    console.trace("📍 Update triggered from:");
    console.groupEnd();

    setState(newValue);
  };
}
